/**
 * Split a user prompt into prose and code segments so machine-appended JSON (factory caller context,
 * alert payloads, API callers) renders as a code block without asking the author to write markdown.
 *
 * Deliberately conservative: a bare block only counts when it opens at a line start, closes with
 * nothing but whitespace after it on that line, and parses as JSON. Fenced blocks are unwrapped
 * whether or not their contents are JSON. Braces inside a sentence are never touched.
 */
export type PromptSegment = { kind: 'text' | 'code'; value: string };

type Block = { value: string; end: number };

const indexOfOrEnd = (text: string, needle: string, from: number): number => {
  const index = text.indexOf(needle, from);
  return index === -1 ? text.length : index;
};

/** Index just past the bracket that closes the one at `start`, honoring JSON strings; -1 if unbalanced. */
const findBlockEnd = (text: string, start: number): number => {
  let depth = 0;
  let inString = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') {
        i++;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === '{' || ch === '[') {
      depth++;
    } else if (ch === '}' || ch === ']') {
      depth--;
      if (depth === 0) {
        return i + 1;
      }
    }
  }
  return -1;
};

const matchFence = (text: string, lineStart: number, lineEnd: number): Block | null => {
  if (!/^[ \t]*```/.test(text.slice(lineStart, lineEnd))) {
    return null;
  }
  let searchStart = lineEnd + 1;
  while (searchStart <= text.length) {
    const nextEnd = indexOfOrEnd(text, '\n', searchStart);
    if (/^[ \t]*```[ \t]*$/.test(text.slice(searchStart, nextEnd))) {
      return { value: text.slice(lineEnd + 1, Math.max(lineEnd + 1, searchStart - 1)), end: nextEnd };
    }
    if (nextEnd >= text.length) {
      break;
    }
    searchStart = nextEnd + 1;
  }
  return null;
};

const matchBareJson = (text: string, lineStart: number, lineEnd: number): Block | null => {
  const indent = /^[ \t]*/.exec(text.slice(lineStart, lineEnd))?.[0].length ?? 0;
  const open = lineStart + indent;
  const ch = text[open];
  if (ch !== '{' && ch !== '[') {
    return null;
  }
  const close = findBlockEnd(text, open);
  if (close < 0) {
    return null;
  }
  const closingLineEnd = indexOfOrEnd(text, '\n', close);
  if (text.slice(close, closingLineEnd).trim() !== '') {
    return null;
  }
  const value = text.slice(open, close);
  try {
    JSON.parse(value);
  } catch {
    return null;
  }
  return { value, end: closingLineEnd };
};

/** Drop the whitespace that separated prose from a code block; the block brings its own spacing. */
const trimBoundaries = (segments: PromptSegment[]): PromptSegment[] =>
  segments
    .map((segment, i) => {
      if (segment.kind !== 'text') {
        return segment;
      }
      let { value } = segment;
      if (segments[i - 1]?.kind === 'code') {
        value = value.replace(/^\s+/, '');
      }
      if (segments[i + 1]?.kind === 'code') {
        value = value.replace(/\s+$/, '');
      }
      return { kind: 'text' as const, value };
    })
    .filter((segment) => segment.kind === 'code' || segment.value.length > 0);

export const splitPromptSegments = (text: string): PromptSegment[] => {
  const segments: PromptSegment[] = [];
  let textStart = 0;
  let lineStart = 0;

  while (lineStart < text.length) {
    const lineEnd = indexOfOrEnd(text, '\n', lineStart);
    const block = matchFence(text, lineStart, lineEnd) ?? matchBareJson(text, lineStart, lineEnd);
    if (block) {
      segments.push({ kind: 'text', value: text.slice(textStart, lineStart) });
      segments.push({ kind: 'code', value: block.value });
      textStart = block.end;
      lineStart = block.end;
      continue;
    }
    lineStart = lineEnd + 1;
  }

  segments.push({ kind: 'text', value: text.slice(textStart) });
  return trimBoundaries(segments);
};
